import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
  ScanLine, ShieldAlert, ShieldCheck, FlaskConical, Leaf, Flame, Droplets, Wine,
  ExternalLink, Sparkles, Loader2, TrendingUp, Beaker, Activity, ImageIcon, ArrowRight,
  Share2, Users, Network, Check, Trophy, Medal, Crown, Mail, Award, Lock,
  ThumbsDown, Target, AlertTriangle, Heart, Utensils, Camera,
} from "lucide-react";
import { api, apiError, authToken } from "@/lib/api";
import { toast } from "sonner";
import { CreditPill } from "@/components/CreditPill";
import { ShareButton } from "@/components/ShareButton";
import { FileUpload } from "@/components/FileUpload";

const GRADE = {
  A: { c: "#22c55e", label: "Excellent" }, B: { c: "#84cc16", label: "Good" },
  C: { c: "#f59e0b", label: "Average" }, D: { c: "#f97316", label: "Poor" },
  F: { c: "#ef4444", label: "Harmful" },
};
const SEV = { high: "#ef4444", medium: "#f59e0b", low: "#84cc16" };

function Macro({ icon: Icon, label, value, unit }) {
  return (
    <div className="rud-card rounded-xl px-1.5 py-3 min-w-0 flex flex-col items-center justify-start text-center" data-testid={`macro-${label.toLowerCase()}`}>
      <Icon size={15} className="text-brand shrink-0" />
      <div className="font-serif text-base sm:text-lg mt-1 leading-none">
        {value}<span className="text-[10px] text-sec">{unit}</span>
      </div>
      <div className="text-[9px] uppercase text-sec leading-tight mt-1 w-full break-words">{label}</div>
    </div>
  );
}

export default function PurityPower({ user, setUser }) {
  const [uploaded, setUploaded] = useState(null);
  const uploadWrapRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const [hub, setHub] = useState(null);
  const [network, setNetwork] = useState(null);
  const [board, setBoard] = useState(null);
  const [digest, setDigest] = useState(null);
  const [sharedIds, setSharedIds] = useState({});
  const [emailing, setEmailing] = useState(false);
  const [community, setCommunity] = useState(null);
  const [nudge, setNudge] = useState(null);

  const loadHub = useCallback(() => {
    if (!user) return;
    api.get("/purity/history").then(({ data }) => setHub(data)).catch(() => {});
    api.get("/purity/network").then(({ data }) => setNetwork(data)).catch(() => {});
    api.get("/purity/leaderboard").then(({ data }) => setBoard(data)).catch(() => {});
    api.get("/purity/digest").then(({ data }) => setDigest(data)).catch(() => {});
    api.get("/purity/community-avoid").then(({ data }) => setCommunity(data)).catch(() => {});
    api.get("/purity/health-nudge").then(({ data }) => setNudge(data)).catch(() => {});
  }, [user]);
  useEffect(() => { loadHub(); }, [loadHub]);

  const downvote = async (idx) => {
    const it = audit?.items?.[idx];
    if (!it) return;
    try {
      const { data } = await api.post("/purity/downvote", { name: it.name, reason: it.flags?.[0]?.ingredient || it.wellness_reason || "", category: it.category });
      setAudit((a) => {
        if (!a) return a;
        const items = a.items.map((x, i) => (i === idx ? { ...x, downvotes: data.downvotes, you_downvoted: data.you_downvoted } : x));
        return { ...a, items };
      });
      toast.success(data.you_downvoted ? "Flagged for the Community Avoid list 👎" : "Removed your flag");
      api.get("/purity/community-avoid").then(({ data }) => setCommunity(data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const shareSignals = () => {
    const link = `${window.location.origin}/signals${user?.referral_code ? `?ref=${user.referral_code}` : ""}`;
    navigator.clipboard.writeText(link).then(() => toast.success("Public signals link copied — share it to grow the collective 🌱")).catch(() => toast.error("Copy failed"));
  };

  const emailSignals = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/purity/digest/email");
      if (data.sent) toast.success(`Weekly Signals sent to ${data.email} ✉️`);
      else if (data.mocked) toast.success(`Queued for ${data.email} — delivery activates once My Kind's email key is configured.`);
      else toast.error(data.error || "Couldn't send right now — try again shortly.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setEmailing(false); }
  };

  const share = async (auditId, shared) => {
    try {
      const { data } = await api.post(`/purity/${auditId}/share`, { shared });
      setSharedIds((m) => ({ ...m, [auditId]: shared }));
      if (shared) {
        toast.success(data.reward ? `Shared to the Product Safety Network · +${data.reward} credits 🙏` : "Shared to the Product Safety Network 🙏");
        if (data.reward && setUser) setUser((u) => u ? { ...u, credits: u.credits + data.reward } : u);
      } else toast.success("Removed from the network");
      loadHub();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const productTitle = (a) => {
    const items = a?.items || [];
    const primary = items.find((i) => i.harmful)?.name || items[0]?.name;
    if (a?.doc_type === "restaurant") return primary ? `${primary} — dish safety` : "Meal safety on My Kind";
    return primary ? `Product Safety of ${primary}` : "My product safety scan";
  };

  // Share a specific scanned product: contribute anonymous signals (credits) AND generate a
  // dynamic, product-specific social banner (e.g. "Product Safety of Pantene — Grade B").
  const shareAudit = async (a) => {
    const id = a.id;
    const title = `${productTitle(a)}${a.wellness_grade ? ` — Grade ${a.wellness_grade}` : ""}`;
    const summary = a.anxya_alert || `A molecular Product Safety scan on My Kind — ${a.toxin_count || 0} flags across ${a.items?.length || 0} item(s). Scan yours free.`;
    try {
      if (!(sharedIds[id] ?? a.shared)) {
        try {
          const { data } = await api.post(`/purity/${id}/share`, { shared: true });
          setSharedIds((m) => ({ ...m, [id]: true }));
          if (data.reward && setUser) setUser((u) => (u ? { ...u, credits: u.credits + data.reward } : u));
        } catch (_) {}
      }
      const { data: sv } = await api.post(`/search/share`, { query: title, kind: "page", summary, href: "/personal?tab=receipts" });
      const link = sv.url;
      if (navigator.share) {
        try { await navigator.share({ title, text: summary, url: link }); }
        catch (_) { await navigator.clipboard.writeText(link); toast.success("Product share link copied 🔗"); }
      } else {
        await navigator.clipboard.writeText(link);
        toast.success("Product share link copied — paste on WhatsApp/Facebook 🔗");
      }
      loadHub();
    } catch (e) { toast.error("Couldn't create the share link — please try again."); }
  };

  if (!user) return <div className="rud-card rounded-2xl p-8 text-center text-sec mt-6" data-testid="purity-signin">Sign in to run a molecular scan of any product label.</div>;

  const scan = async () => {
    if (!uploaded) { toast.error("Click or upload a product label photo first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/purity/scan", { file_url: uploaded.absoluteUrl });
      setAudit(data.audit);
      if (setUser) setUser((u) => u ? { ...u, credits: data.credits_remaining, free_used: { ...(u.free_used || {}), scan: true } } : u);
      if (data.was_free) toast.success("Your 1 free molecular scan ✨ — next scans are fair-priced for your region.");
      if (data.audit.anxya_alert) toast.success(data.audit.anxya_alert);
      if (data.streak?.new_week) toast.success(`🔥 Weekly scan streak: ${data.streak.weeks} week${data.streak.weeks > 1 ? "s" : ""}! +${data.streak.reward} vault credits`);
      if (data.streak?.milestone) toast.success(`🏆 ${data.streak.milestone}-week milestone unlocked! +${data.streak.milestone_bonus} bonus vault credits`);
      loadHub();
    } catch (e) { if (e.response?.status !== 402) toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const g = audit ? (GRADE[audit.wellness_grade] || GRADE.C) : null;
  const t = audit?.totals || {};

  return (
    <div className="mt-6 flex flex-col gap-5" data-testid="purity-power">
      {/* Scanner dashboard */}
      <div className="rud-card rounded-2xl p-6 relative overflow-hidden order-first">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-brand/10 blur-2xl" />
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><FlaskConical size={14} /> The Scanner · Molecular Audit</div>
          <ShareButton slug="safety" title="Product Safety · My Kind" text="Scan any product label for toxins on My Kind." label="Share" className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5" />
        </div>
        <h3 className="font-serif text-2xl mt-2">Click &amp; Upload a Product Label</h3>
        <p className="text-sec text-sm mt-1 max-w-xl">
          Click a photo or upload a product / ingredient label. My Kind flags harmful ingredients &amp; toxic chemicals in food, personal care, cosmetics &amp; perfumes (parabens, phthalates, SLS, PFAS, synthetic fragrance &amp; more) — with a scientific reason &amp; reference, tallies calories, macros, sugar &amp; alcohol, checks every item against <span className="italic font-bold text-[var(--rud-text)]">your health goals &amp; habits</span>, grades it, and suggests cleaner, non-toxic swaps. Thumbs-down anything toxic to warn the community.<br />
          <span className="italic font-bold text-[var(--rud-text)]">Privacy-first: we read the label only — nothing else.</span>
        </p>

        {/* 3 compact steps — steps 1 & 2 are clickable shortcuts */}
        <div className="grid grid-cols-3 gap-2.5 sm:gap-3 mt-4" data-testid="scan-steps">
          {[
            { n: 1, Icon: Camera, title: "Upload the Product Ingredients Label", desc: "", onClick: () => uploadWrapRef.current?.querySelector("button")?.click(), disabled: false },
            { n: 2, Icon: ScanLine, title: "Run Ingredients Molecular Scan", desc: "Identify harmful ingredients & toxic chemicals instantly.", onClick: scan, disabled: busy || !uploaded },
            { n: 3, Icon: Share2, title: "See, Save & Share the Results", desc: "Get your grade, cleaner swaps — and warn the community." },
          ].map(({ n, Icon, title, desc, onClick, disabled }) => {
            const clickable = !!onClick;
            return (
            <div
              key={n}
              data-testid={`scan-step-${n}`}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-disabled={disabled || undefined}
              onClick={clickable && !disabled ? onClick : undefined}
              onKeyDown={clickable && !disabled ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
              className={`rounded-xl border border-rud bg-white/5 p-3 relative overflow-hidden transition-colors ${clickable ? "cursor-pointer hover:border-brand hover:bg-brand/5" : ""} ${disabled ? "opacity-50 cursor-not-allowed hover:border-rud hover:bg-white/5" : ""}`}
            >
              <div className="absolute top-1.5 right-2.5 font-serif text-2xl text-brand/15 leading-none select-none">{n}</div>
              <div className="grid place-items-center w-8 h-8 rounded-lg bg-brand/15 text-brand"><Icon size={16} /></div>
              <div className="font-serif text-[13px] sm:text-base font-bold tracking-tight mt-2 leading-snug">{title}</div>
              {desc && <p className="text-sec text-[11px] sm:text-xs mt-1 leading-relaxed">{desc}</p>}
              {clickable && !disabled && <span className="text-[10px] uppercase tracking-wider text-brand mt-1.5 inline-block">Tap here →</span>}
            </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          <span ref={uploadWrapRef} className="contents">
            <FileUpload category="purity" accept="image/*" label={uploaded ? "Change photo" : "Click / upload product label"} testId="purity-upload" onUploaded={(f) => { setUploaded(f); setAudit(null); }} />
          </span>
          {uploaded && <span className="text-xs text-sec flex items-center gap-1.5" data-testid="purity-file-name"><ImageIcon size={13} className="text-brand" /> {uploaded.original_filename || "Ready to scan"}</span>}
          <button data-testid="purity-scan-btn" onClick={scan} disabled={busy || !uploaded}
            className="btn-primary h-10 px-5 rounded-full text-sm flex items-center gap-2 disabled:opacity-50 ml-auto">
            {busy ? <><Loader2 size={15} className="animate-spin" /> Scanning molecules…</> : <><ScanLine size={15} /> Run molecular scan {user && <CreditPill cost={Number(user.scan_cost ?? 0.5)} free={!user.free_used?.scan} />}</>}
          </button>
        </div>
        {uploaded && <div className="mt-4"><img src={`${uploaded.absoluteUrl}?auth=${authToken.get() || ""}`} alt="upload preview" className="max-h-48 rounded-lg border border-rud" /></div>}
      </div>

      {/* Your Scanned Products — always shown right below the upload box */}
      <div className="rud-card rounded-2xl p-6 order-1" data-testid="scanned-products">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><ScanLine size={14} /> Your Scanned Products</div>
        <p className="text-sec text-sm mt-1">Every product &amp; label you've scanned — tap Share to post its safety result with its own banner.</p>
        {hub?.audits?.length > 0 ? (
          <div className="mt-4 space-y-2">
            {hub.audits.map((a) => {
              const items = a.items || [];
              const name = items.find((i) => i.harmful)?.name || items[0]?.name || (a.doc_type === "label" ? "Product label" : "Receipt");
              const gr = GRADE[a.wellness_grade] || GRADE.C;
              return (
                <div key={a.id} data-testid={`scanned-${a.id}`} className="flex items-center gap-3 rud-card rounded-xl p-3">
                  {a.image_url
                    ? <img src={a.image_url} alt={name} data-testid={`scanned-img-${a.id}`} className="w-12 h-12 rounded-lg object-cover border border-rud shrink-0" />
                    : <span className="font-serif text-lg w-8 text-center shrink-0" style={{ color: gr.c }}>{a.wellness_grade}</span>}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      {name}
                      {a.image_url && <span className="text-[11px] font-serif px-1.5 rounded" style={{ color: gr.c }}>{a.wellness_grade}</span>}
                    </div>
                    <div className="text-[11px] text-sec">{items.length} item{items.length === 1 ? "" : "s"} · {a.toxin_count} flags · {new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <button data-testid={`scanned-share-${a.id}`} onClick={() => shareAudit(a)}
                    className={`text-[11px] px-3 py-1.5 rounded-full flex items-center gap-1 shrink-0 ${(sharedIds[a.id] ?? a.shared) ? "bg-brand/20 text-brand" : "btn-primary"}`}>
                    {(sharedIds[a.id] ?? a.shared) ? <><Check size={12} /> Shared</> : <><Share2 size={12} /> Share</>}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mt-4 text-sec text-sm" data-testid="scanned-empty">No scans yet — upload a product label above and your scanned products will appear here with a thumbnail you can tap to share. 🧪</p>
        )}
      </div>

      {/* Weekly Signals Digest */}
      {digest && (digest.top_toxins.length > 0 || digest.top_picks.length > 0) && (
        <div className="rud-card rounded-2xl p-6 order-3" data-testid="signals-digest">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Network size={14} /> Weekly Signals Digest · {digest.week_label}</div>
            <div className="flex items-center gap-2">
              <button data-testid="email-signals-btn" onClick={emailSignals} disabled={emailing} className="btn-primary rounded-full text-xs h-8 px-3 flex items-center gap-1.5 disabled:opacity-60">
                {emailing ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Email me this week's signals
              </button>
              <button data-testid="share-signals-btn" onClick={shareSignals} className="btn-ghost rounded-full text-xs h-8 px-3 flex items-center gap-1.5"><Share2 size={12} /> Share signals page</button>
            </div>
          </div>
          <p className="text-sec text-sm mt-1">{digest.period === "this week" ? "This week's" : "The collective's"} top toxins to avoid &amp; cleaner picks — from {digest.scans} shared scans.</p>
          <div className="grid md:grid-cols-2 gap-5 mt-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-sec mb-2 flex items-center gap-1.5"><ShieldAlert size={13} className="text-red-400" /> Avoid this week</div>
              <div className="space-y-1.5">
                {digest.top_toxins.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm" data-testid={`digest-toxin-${i}`}><span className="w-4 text-sec text-xs">{i + 1}</span><span className="flex-1 truncate">{t.name}</span><span className="text-[11px] text-sec">{t.count}×</span></div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-sec mb-2 flex items-center gap-1.5"><Leaf size={13} className="text-green-400" /> Buy instead</div>
              <div className="space-y-1.5">
                {digest.top_picks.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm" data-testid={`digest-pick-${i}`}><span className="w-4 text-sec text-xs">{i + 1}</span><span className="flex-1 truncate">{p.name}</span><Leaf size={12} className="text-green-400 shrink-0" /></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weekly personalized health nudge */}
      {nudge && nudge.nudge && (
        <div className="rud-card rounded-2xl p-5 border border-brand/25 order-5" data-testid="health-nudge">
          <div className="flex items-start gap-3">
            <Heart size={18} className="text-brand shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest text-brand">This week · Your My Kind health nudge</div>
              <div className="text-sm mt-1">{nudge.nudge}</div>
              {nudge.stats?.scans > 0 && (
                <div className="text-[11px] text-sec mt-2">This month: {nudge.stats.scans} scans · ~{nudge.stats.calories} kcal · ~{nudge.stats.sugar_g}g sugar · {nudge.stats.toxins} flags</div>
              )}
              {!nudge.has_context && (
                <div className="text-[11px] text-sec mt-1.5">Add a health goal in <span className="text-brand">My Habits</span> and I'll tailor every scan to it.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Community Avoid — most-downvoted products/dishes */}
      {community && community.items && community.items.length > 0 && (
        <div className="rud-card rounded-2xl p-6 order-6" data-testid="community-avoid">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><ThumbsDown size={14} /> Community Avoid · Most downgraded</div>
          <p className="text-sec text-sm mt-1">Products &amp; dishes the My Kind community has flagged the most. Your thumbs-down helps everyone eat cleaner.</p>
          <div className="grid sm:grid-cols-2 gap-2 mt-4">
            {community.items.slice(0, 10).map((c, i) => (
              <div key={i} data-testid={`avoid-${i}`} className="flex items-center gap-2 rounded-xl border border-rud p-3">
                <span className="w-5 text-sec text-xs">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  {c.reason && <div className="text-[11px] text-sec truncate">{c.reason}</div>}
                </div>
                <span className="text-[11px] text-red-400 flex items-center gap-1 shrink-0"><ThumbsDown size={11} /> {c.downvotes}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {audit && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5 order-2" data-testid="purity-result">
          {/* My Kind alert */}
          <div className="flex items-start gap-3 rounded-2xl border border-brand/30 bg-brand/10 p-4" data-testid="anxya-alert">
            <Sparkles size={18} className="text-brand shrink-0 mt-0.5" />
            <div className="flex-1"><div className="text-[11px] uppercase tracking-widest text-brand">My Kind</div><div className="text-sm">{audit.anxya_alert}</div></div>
            <button data-testid="purity-share-btn" onClick={() => shareAudit(audit)}
              className={`shrink-0 h-9 px-4 rounded-full text-xs flex items-center gap-1.5 ${sharedIds[audit.id] ? "bg-brand/20 text-brand" : "btn-primary"}`}>
              {sharedIds[audit.id] ? <><Check size={13} /> Shared</> : <><Share2 size={13} /> Save &amp; share</>}
            </button>
          </div>
          <p className="text-[11px] text-sec -mt-2">Sharing contributes anonymous signals (no personal data) to the collective Product Safety Network so everyone spots harmful products and cleaner switches faster.</p>

          {/* Wellness grade + totals */}
          <div className="grid md:grid-cols-[170px,1fr] gap-4 items-start">
            <div className="rud-card rounded-2xl p-6 flex flex-col items-center justify-center text-center" data-testid="wellness-grade">
              <div className="text-[10px] uppercase tracking-widest text-sec">Wellness grade</div>
              <div className="font-serif leading-none mt-1" style={{ fontSize: "4.5rem", color: g.c }}>{audit.wellness_grade}</div>
              <div className="text-sm font-medium" style={{ color: g.c }}>{g.label}</div>
              <div className="text-[11px] text-sec mt-2">{audit.wellness_reason}</div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              <Macro icon={Flame} label="Calories" value={Math.round(t.calories || 0)} unit=" kcal" />
              <Macro icon={Activity} label="Protein" value={Math.round(t.protein_g || 0)} unit="g" />
              <Macro icon={Droplets} label="Fat" value={Math.round(t.fat_g || 0)} unit="g" />
              <Macro icon={Beaker} label="Carbs" value={Math.round(t.carbs_g || 0)} unit="g" />
              <Macro icon={Droplets} label="Sugar" value={Math.round(t.sugar_g || 0)} unit="g" />
              <Macro icon={Wine} label="Alcohol" value={(t.alcohol_std_drinks || 0)} unit=" dr" />
            </div>
          </div>

          {/* Personalized coaching + healthier cart */}
          {(audit.personal_alert || (audit.healthier_cart && audit.healthier_cart.length > 0)) && (
            <div className="rud-card rounded-2xl p-5 border border-brand/30" data-testid="personal-coaching">
              {audit.personal_alert && (
                <div className="flex items-start gap-3">
                  <Heart size={18} className="text-brand shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-brand">Your personal health coach</div>
                    <div className="text-sm mt-0.5" data-testid="personal-alert">{audit.personal_alert}</div>
                  </div>
                </div>
              )}
              {audit.healthier_cart && audit.healthier_cart.length > 0 && (
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-widest text-sec mb-2 flex items-center gap-1.5"><Leaf size={13} className="text-green-400" /> Healthier {audit.doc_type === "restaurant" ? "meal" : "basket"} — swap suggestions</div>
                  <div className="flex flex-wrap gap-2" data-testid="healthier-cart">
                    {audit.healthier_cart.map((s, i) => (
                      <span key={i} data-testid={`healthier-swap-${i}`} className="text-xs px-3 py-1.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/25">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Item molecular audit */}
          <div>
            <div className="flex items-center gap-2 text-sm font-medium mb-2">
              {audit.doc_type === "restaurant" ? <Utensils size={16} className="text-brand" /> : <ShieldAlert size={16} className="text-brand" />}
              {audit.doc_type === "restaurant" ? "Menu audit · " : "Molecular audit · "}{audit.items.length} {audit.doc_type === "restaurant" ? "dishes" : "items"} · {audit.toxin_count} flags
            </div>
            <div className="space-y-3">
              {audit.items.map((it, i) => (
                <div key={i} className={`rud-card rounded-2xl p-4 border ${it.harmful ? "border-red-500/40" : "border-rud"}`} data-testid={`purity-item-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        {it.harmful ? <ShieldAlert size={15} className="text-red-400 shrink-0" /> : <ShieldCheck size={15} className="text-green-400 shrink-0" />}
                        {it.name}{it.is_alcohol && <Wine size={13} className="text-amber-400" />}
                        {it.matches_goals === false && (
                          <span data-testid={`offgoal-${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 flex items-center gap-1"><Target size={10} /> Off your goals</span>
                        )}
                        {it.matches_goals === true && it.goal_note && it.is_food && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30 flex items-center gap-1"><Target size={10} /> Fits your goals</span>
                        )}
                        {it.allergy_alert && (
                          <span data-testid={`allergy-${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 flex items-center gap-1"><AlertTriangle size={10} /> Allergy alert</span>
                        )}
                      </div>
                      <div className="text-[11px] text-sec mt-0.5">{it.category || (it.is_food ? "Food" : "Product")}{it.is_food ? ` · ${Math.round(it.calories)} kcal · P${Math.round(it.protein_g)} F${Math.round(it.fat_g)} C${Math.round(it.carbs_g)} · Sugar ${Math.round(it.sugar_g)}g` : ""}</div>
                      {it.goal_note && <div className={`text-[11px] mt-1 ${it.matches_goals === false ? "text-amber-400" : "text-sec"}`} data-testid={`goal-note-${i}`}>{it.goal_note}</div>}
                      {it.allergy_alert && it.allergy_note && <div className="text-[11px] mt-0.5 text-red-400">{it.allergy_note}</div>}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-sec">Product Safety</div>
                        <div className="font-serif text-xl" style={{ color: it.purity_score >= 70 ? "#22c55e" : it.purity_score >= 40 ? "#f59e0b" : "#ef4444" }}>{it.purity_score}</div>
                      </div>
                      <button data-testid={`downvote-${i}`} onClick={() => downvote(i)}
                        title="Downgrade this product for the community"
                        className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] border transition-colors ${it.you_downvoted ? "bg-red-500/20 text-red-400 border-red-500/40" : "border-rud text-sec hover:text-red-400 hover:border-red-500/40"}`}>
                        <ThumbsDown size={12} /> {it.downvotes || 0}
                      </button>
                    </div>
                  </div>

                  {it.flags.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {it.flags.map((f, j) => (
                        <div key={j} className="flex items-start gap-2 text-xs" data-testid={`purity-flag-${i}-${j}`}>
                          <span className="mt-0.5 w-2 h-2 rounded-full shrink-0" style={{ background: SEV[f.severity] }} />
                          <span className="text-sec"><b className="text-[var(--rud-text)]">{f.ingredient}</b> — {f.reason} {f.reference_url && <a href={f.reference_url} target="_blank" rel="noopener noreferrer" className="text-brand inline-flex items-center gap-0.5 hover:underline" data-testid={`purity-ref-${i}-${j}`}>ref <ExternalLink size={10} /></a>}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {it.cleaner_choice && (
                    <div data-testid={`smart-switch-${i}`}
                      className="mt-3 flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/10 p-3">
                      <Leaf size={18} className="text-green-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-green-400">The Smart Switch · Cleaner choice</div>
                        <div className="text-sm font-medium truncate">{it.cleaner_choice.name}</div>
                        {it.cleaner_choice.reason && <div className="text-[11px] text-sec truncate">{it.cleaner_choice.reason}</div>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Health Hub — monthly tracker */}
      {hub && (
        <div className="rud-card rounded-2xl p-6 order-4" data-testid="health-hub">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><TrendingUp size={14} /> Health Hub · This month</div>
            {hub.streak && (
              <div className="flex items-center gap-1.5 text-sm rud-card rounded-full px-3 py-1" data-testid="scan-streak">
                <Flame size={14} className={hub.streak.active ? "text-brand" : "text-sec"} />
                <span>{hub.streak.weeks}-week streak</span>
                <span className="text-[11px] text-sec">· best {hub.streak.best}</span>
              </div>
            )}
          </div>
          {hub.streak?.badges && (
            <div className="mt-4" data-testid="streak-badges">
              <div className="text-[11px] uppercase tracking-widest text-sec mb-2 flex items-center gap-1.5"><Award size={13} className="text-brand" /> Streak milestones</div>
              <div className="flex flex-wrap items-center gap-2">
                {hub.streak.badges.map((b) => (
                  <div key={b.weeks} data-testid={`streak-badge-${b.weeks}`}
                    className={`flex items-center gap-2 rounded-full pl-1.5 pr-3.5 py-1.5 border transition-all ${b.earned ? "border-brand/50 bg-gradient-to-r from-brand/25 to-brand/5 text-[var(--rud-text)] shadow-[0_0_20px_-6px_var(--rud-brand)]" : "border-rud bg-elevated/40 text-sec opacity-70"}`}>
                    <span className={`grid place-items-center w-7 h-7 rounded-full shrink-0 ${b.earned ? "bg-brand text-black" : "bg-elevated text-sec"}`}>
                      {b.earned ? <Award size={15} /> : <Lock size={12} />}
                    </span>
                    <div className="leading-tight">
                      <div className="text-sm font-medium">{b.label} <span className="text-[11px] text-sec font-normal">· {b.title}</span></div>
                      <div className="text-[10px] text-sec">{b.earned ? "Unlocked 🏆" : `Reach ${b.weeks} weeks · +${b.bonus} vault`}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
            <Macro icon={ScanLine} label="Scans" value={hub.month.scans} unit="" />
            <Macro icon={Flame} label="Calories" value={hub.month.calories} unit="" />
            <Macro icon={Droplets} label="Sugar" value={hub.month.sugar_g} unit="g" />
            <Macro icon={ShieldAlert} label="Toxins" value={hub.month.toxins} unit="" />
            <Macro icon={Wine} label="Alcohol" value={hub.month.alcohol_std_drinks} unit=" dr" />
          </div>
        </div>
      )}

      {/* Product Safety Network — collective intelligence */}
      {network && network.scans > 0 && (
        <div className="rud-card rounded-2xl p-6 order-7" data-testid="purity-network">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Network size={14} /> Product Safety Network · Collective signals</div>
          <p className="text-sec text-sm mt-1">Anonymous, crowd-sourced intelligence from every audit members choose to share — a collective, data-sovereign network for cleaner living.</p>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <Macro icon={ScanLine} label="Shared scans" value={network.scans} unit="" />
            <Macro icon={Users} label="Contributors" value={network.contributors} unit="" />
            <Macro icon={ShieldAlert} label="Toxins caught" value={network.toxins} unit="" />
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-5">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-sec mb-2 flex items-center gap-1.5"><ShieldAlert size={13} className="text-red-400" /> Most-flagged ingredients</div>
              {network.top_flagged.length === 0 ? <div className="text-sec text-sm">No signals yet.</div> : (
                <div className="space-y-1.5">
                  {network.top_flagged.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm" data-testid={`net-flag-${i}`}>
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-[11px] text-sec">{f.count}×</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-sec mb-2 flex items-center gap-1.5"><Leaf size={13} className="text-green-400" /> Top cleaner switches</div>
              {network.top_switches.length === 0 ? <div className="text-sec text-sm">No suggestions yet.</div> : (
                <div className="space-y-1.5">
                  {network.top_switches.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm" data-testid={`net-switch-${i}`}>
                      <span className="flex-1 truncate">{s.name}</span>
                      <span className="text-[11px] text-sec">{s.count}×</span>
                      <Leaf size={12} className="text-green-400 shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-5">
            {["A", "B", "C", "D", "F"].map((g) => (
              <div key={g} className="flex-1 text-center" data-testid={`net-grade-${g}`}>
                <div className="rounded-lg py-2 text-sm font-serif" style={{ background: `${(GRADE[g] || GRADE.C).c}22`, color: (GRADE[g] || GRADE.C).c }}>{network.grades[g] || 0}</div>
                <div className="text-[10px] text-sec mt-0.5">Grade {g}</div>
              </div>
            ))}
          </div>

          {board && board.leaderboard.length > 0 && (
            <div className="mt-6 pt-5 border-t border-rud" data-testid="purity-leaderboard">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-widest text-sec flex items-center gap-1.5"><Trophy size={13} className="text-brand" /> Top contributors</div>
                <div className="text-[11px] text-sec">Earn <b className="text-brand">+{board.share_reward}</b> credits per shared scan{board.my_rank ? ` · you're #${board.my_rank}` : ""}</div>
              </div>
              <div className="space-y-1.5">
                {board.leaderboard.map((r) => (
                  <div key={r.rank} className={`flex items-center gap-3 rounded-lg px-3 py-2 border ${r.is_me ? "border-brand/40 bg-brand/5" : "border-rud"}`} data-testid={`leader-${r.rank}`}>
                    <span className="w-6 text-center shrink-0">
                      {r.rank === 1 ? <Crown size={16} className="text-brand mx-auto" /> : r.rank <= 3 ? <Medal size={15} className="mx-auto" style={{ color: r.rank === 2 ? "#c0c0c0" : "#cd7f32" }} /> : <span className="text-sm text-sec">{r.rank}</span>}
                    </span>
                    <span className="flex-1 truncate text-sm">{r.name}{r.is_me && <span className="text-brand text-[11px]"> · you</span>}</span>
                    <span className="text-[11px] text-sec shrink-0">{r.toxins} toxins</span>
                    <span className="text-sm shrink-0"><b>{r.scans}</b> <span className="text-sec text-[11px]">scans</span></span>
                    <span className="text-brand text-xs shrink-0 w-14 text-right">+{r.earned}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
